<template>
  <div class="sales-search-view">
    <header>
      <a href="https://www.cardladder.com">
        <full-logo pro />
      </a>

      <btn as="a" kind="secondary" @click="$router.push({ path: '/login', query: $route.query })">
        Login
      </btn>
    </header>

    <div class="container">
      <div v-show="sales.length">
        <h2 class="secondary-font">
          {{ sales.length }} Most Recent {{ sales.length === 1 ? 'Sale' : 'Sales' }}
        </h2>
      </div>

      <spinner v-if="!loaded" />

      <div v-else class="results">
        <empty-state
          v-if="showEmptyState"
          icon="search_off"
          title="There are no results for your query"
        />

        <template v-else>
          <div class="list">
            <sales-list-item
              v-for="(item, i) in sales"
              :key="i"
              as="a"
              :item="item"
              :href="item.url"
              target="_blank"
            >
              <align small-gap>
                <verified-icon v-if="item.cardId || item.memberVerified" />
              </align>
            </sales-list-item>
          </div>
        </template>
      </div>

      <section v-if="loaded" class="upsell">
        <div class="text-upsell">
          <h3 class="secondary-font">
            Wanna see more results?
          </h3>

          <p>Get the estimated price for any card that sold in the past by simply searching for the card in our massive database. Our software can also identify the price relationship between the card and the player's index on that date and use it to forecast the card's current value.</p>

          <div class="button-upsell">
            <div>
              <btn as="a" @click="$router.push({ path: '/signup', query: $route.query })">
                Upgrade to Card Ladder Pro
              </btn>

              <div>
                <small>with a 7-day free trial</small>
              </div>
            </div>
          </div>
        </div>

        <img src="@/assets/upsell-sales-history.png" />
      </section>
    </div>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';
import { mapGetters } from 'vuex';

import SalesListItem from '@cardladder/shared/components/SalesListItem';
import VerifiedIcon from '@cardladder/shared/components/VerifiedIcon';
import FullLogo from '@cardladder/shared/components/FullLogo';

export default {
  components: {
    SalesListItem,
    VerifiedIcon,
    FullLogo,
  },

  data() {
    return {
      limit: 5,
      sales: [],
      loading: false,
      loaded: false,
    };
  },

  mounted() {
    this.checkRedirect();
  },

  computed: {
    ...mapGetters({
      isMember: 'isMember',
    }),

     showEmptyState() {
      return !this.loading && this.loaded && this.sales.length === 0;
    },
  },

  methods: {
    async checkRedirect() {
      if (!this.$route.query) {
        this.$router.replace('/dashboard');
        return;
      }

      const routeQuery = this.$route.query;

      if (!routeQuery.image && !routeQuery.gemRateId && !routeQuery.universalGemRateId && !routeQuery.cert && !routeQuery.specid) {
        this.$router.replace('/dashboard');
        return;
      }

      if (routeQuery.image) {
        this.$router.replace({ path: '/sales-history', query: { image: routeQuery.image } });
        return;
      }

      const filtersRequest = {};

      if (routeQuery.cert) {
        filtersRequest.cert = routeQuery.cert;
      }

      if (routeQuery.grader) {
        filtersRequest.grader = routeQuery.grader;
      }

      if (routeQuery.grade) {
        filtersRequest.grade = routeQuery.grade;
      }

      if (routeQuery.specid) {
        filtersRequest.psaSpecId = routeQuery.specid;
      }

      if (routeQuery.universalGemRateId) {
        filtersRequest.universalGemRateId = routeQuery.universalGemRateId;
      }

      if (routeQuery.gemRateId) {
        filtersRequest.gemRateId = routeQuery.gemRateId;
      }

      if (routeQuery.qualifierType) {
        filtersRequest.qualifierType = routeQuery.qualifierType;
      }

      if (routeQuery.autographGrade) {
        filtersRequest.autographGrade = routeQuery.autographGrade;
      }

      const filters = Object.keys(filtersRequest).map((key) => `${key}:${filtersRequest[key]}`).join('|');

      if (this.isMember) {
        if (routeQuery.cert && routeQuery.grader) {
          this.$router.replace({ path: '/sales-history', query: { cert: routeQuery.cert, grader: routeQuery.grader } });
          return;
        }

        let query = {};

        if (routeQuery.image) {
          query.image = routeQuery.image;
        }

        if (filters) {
          query = { filters };
        }

        this.$router.replace({ path: '/sales-history', query });
      } else {
        this.search(filtersRequest);
      }
    },

    async search(filtersRequest) {
      try {
        this.loading = true;

        const { data: salesData } = await firebase.functions().httpsCallable('httpprofilesales')(filtersRequest);

        this.sales = salesData.sales.map((sale) => {
          return {
            ...sale,
            url: sale.platform === 'eBay' ? `${sale.url}?nordt=true&rt=nc&orig_cvip=true` : sale.url,
          };
        });
      } catch (e) {
        // swallow
      }

      this.loaded = true;
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.sales-search-view {
  padding: $gp $gp 0;
  height: 100%;
  display: flex;
  flex-direction: column;

  h2 {
    margin-bottom: 0;
  }

  p {
    color: $color-gray-5;
  }
}

header {
  display: flex;
  justify-content: space-between;
}

.container {
  @include container;
  padding-bottom: 0;
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.upsell {
  margin-top: $gp * 2;
  display: grid;
  grid-gap: $gp;
  grid-template-columns: 3fr 2fr;

  @media($medium-down) {
    grid-template-columns: 1fr;
  }
}

.button-upsell {
  display: flex;

  @media($medium-down) {
    justify-content: center;
  }

  small {
    display: block;
    margin-top: 5px;
    text-align: center;
  }
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}
</style>
